import { ref, toRaw } from 'vue'
import { defineStore } from 'pinia'
import type { BetslipView } from '@nsftx/seven-client-sdk-integrator'
import { logService } from '@nsftx/seven-client-retail-utils'
import TicketResolveError from '#src/classes/TicketResolveError'
import { createBetslipChecker } from '#src/services/betslipCheckerService'
import type { GameLinkView } from '#src/interfaces/gameManager'
import { getGameDisplayName } from '#src/utils/gameUtils'
import { getBetslipBarcode, getBetslipPayment } from '#src/utils/betslipUtils'
import { pluginState } from '#src/sevenIgamingPlugin'
import errorParser from '#src/utils/errorParser'
import { LocalTicketStatus, type ClientTicket, type ClientTicketActions } from './types'

const LOG_PREFIX = '[ticketsStore]'

export const useTicketsStore = defineStore('ticketsStore', () => {
  const tickets = ref<ClientTicket[]>([])
  const action = ref<ClientTicketActions | null>(null)
  const ticketCheckers = ref<Record<string, ReturnType<typeof createBetslipChecker>>>({})
  const pendingActions = ref<Record<string, boolean>>({})

  const getStorageTicket = (ticket: ClientTicket) => {
    const storedTicket = { ...toRaw(ticket) }
    delete storedTicket['betslip']

    return storedTicket
  }

  const addTicketToStorage = async (ticket: ClientTicket) => {
    const slipsStorageDriver = pluginState.slipsStorageDriver
    if (!slipsStorageDriver) {
      return
    }

    const storedTicket = getStorageTicket(ticket)
    const existingTicket = await slipsStorageDriver.getById(storedTicket.feBetslipId)
    if (existingTicket) {
      return
    }

    return slipsStorageDriver.add(storedTicket)
  }

  const updateStorageTicket = (ticket: ClientTicket) => {
    const slipsStorageDriver = pluginState.slipsStorageDriver
    if (!slipsStorageDriver) {
      return
    }

    return slipsStorageDriver.update(getStorageTicket(ticket))
  }

  const syncTicketWithStorage = (syncTicket?: Promise<ClientTicket | undefined>) => {
    syncTicket?.catch((error) => {
      logService.error(`${LOG_PREFIX} Failed to sync ticket with IndexedDB.`, {
        code: 'SGP_BETSLIP_TICKETS_SYNC_ERROR',
        ...errorParser.parseUpstream(error)
      })
    })
  }

  const getTicketByFeBetslipId = (feBetslipId: string) =>
    tickets.value.find((ticket) => ticket.feBetslipId === feBetslipId)

  const saveTicket = (ticket: ClientTicket) => {
    if (getTicketByFeBetslipId(ticket.feBetslipId)) {
      return
    }

    tickets.value.unshift(ticket)
    syncTicketWithStorage(addTicketToStorage(ticket))
  }

  /**
   * @throws {TicketResolveError}
   */
  const resolveTicket = (betslip: BetslipView) => {
    const feBetslipId = betslip.context.feBetslipId

    if (!feBetslipId) {
      throw new TicketResolveError('FE betslip id is missing.', 'SGP_TICKET_NOT_FOUND', {
        context: {
          betslipId: betslip.id
        }
      })
    }

    const ticket = getTicketByFeBetslipId(feBetslipId)

    if (!ticket) {
      throw new TicketResolveError('Ticket not found in store.', 'SGP_TICKET_NOT_FOUND', {
        context: {
          feBetslipId
        }
      })
    }

    if (isTicketResolved(feBetslipId)) {
      throw new TicketResolveError('Ticket is already resolved.', 'SGP_TICKET_ALREADY_RESOLVED', {
        context: {
          feBetslipId
        }
      })
    }

    acceptTicket(feBetslipId, betslip)

    return betslip
  }

  const isTicketResolved = (feBetslipId: string) => {
    const ticket = getTicketByFeBetslipId(feBetslipId)

    if (!ticket) {
      return false
    }

    if (action.value === 'add' && ticket.localStatus === LocalTicketStatus.Pending) {
      return false
    }

    return ticket
  }

  const addPendingTicket = (feBetslipId: string, gameLink: GameLinkView) => {
    const pendingTicket = {
      feBetslipId,
      linkCode: gameLink.code,
      gameName: getGameDisplayName(gameLink),
      actionType: 'BETSLIP_PAYIN',
      timestamp: Date.now(),
      localStatus: LocalTicketStatus.Pending,
      amount: null,
      currency: null,
      barcode: null,
      bets: null
    } satisfies ClientTicket

    addPendingAction(feBetslipId)
    saveTicket(pendingTicket)
  }

  const acceptTicket = (feBetslipId: string, betslip: BetslipView) => {
    const ticket = getTicketByFeBetslipId(feBetslipId)
    if (!ticket) {
      return
    }

    ticket.localStatus = LocalTicketStatus.Accepted
    ticket.betslip = betslip
    ticket.currency = betslip.context.currency
    ticket.bets = betslip.bets.map((bet) => ({
      id: bet.id
    }))
    ticket.barcode = getBetslipBarcode(betslip)
    ticket.amount = getBetslipPayment(betslip.bets)

    removePendingAction(feBetslipId)
    syncTicketWithStorage(updateStorageTicket(ticket))
  }

  const startChecker = async (feBetslipId: string) => {
    ticketCheckers.value[feBetslipId] = createBetslipChecker(feBetslipId)

    return ticketCheckers.value[feBetslipId].startChecker()
  }

  const endChecker = (feBetslipId: string) => {
    const ticketChecker = ticketCheckers.value[feBetslipId]
    if (!ticketChecker) {
      return
    }

    ticketChecker.endChecker()
    delete ticketCheckers.value[feBetslipId]
  }

  const setFailedTicket = (feBetslipId: string) => {
    const ticket = getTicketByFeBetslipId(feBetslipId)
    if (!ticket) {
      throw new TicketResolveError('Ticket not found in store.', 'SGP_TICKET_NOT_FOUND', {
        context: {
          feBetslipId
        }
      })
    }

    ticket.localStatus = LocalTicketStatus.Failed
    removePendingAction(feBetslipId)
    syncTicketWithStorage(updateStorageTicket(ticket))
  }

  const addPendingAction = (feBetslipId: string) => {
    pendingActions.value[feBetslipId] = true
  }

  const removePendingAction = (feBetslipId: string) => {
    if (pendingActions.value[feBetslipId]) {
      delete pendingActions.value[feBetslipId]
    }
  }

  const isActionPending = (feBetslipId: string) => !!pendingActions.value[feBetslipId]

  const setAction = (ticketAction: ClientTicketActions) => {
    action.value = ticketAction
  }

  return {
    tickets,
    isActionPending,
    addPendingTicket,
    resolveTicket,
    acceptTicket,
    isTicketResolved,
    startChecker,
    endChecker,
    setFailedTicket,
    setAction
  }
})
